import {useFormContext} from "react-hook-form";
import * as Yup from "yup";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {ScheduleEntries} from "@/components/form/maintenance/ScheduleEntries.tsx";
import {ScheduleEntryInput, ScheduleEntryType} from "@/components/form/maintenance/types.ts";
import {ScheduleState} from "@/lib/types/checks.ts";
import {CheckFormInfo} from "@/pages/checks/components/form/CheckFormInfo.tsx";
import {OnCallHoursRadioGroup} from "@/pages/contacts/components/form/fields/OnCallHoursRadioGroup.tsx";

export interface OnCallHoursFormValues {
  schedule_entries: ScheduleEntryInput[];
}

const onceDateErrors = (entries: ScheduleEntryInput[] | undefined, arrayPath: string) => {
  const errors: Yup.ValidationError[] = [];
  (entries ?? []).forEach((entry, index) => {
    if (entry.type !== ScheduleEntryType.ONCE) {
      return;
    }
    const at = (field: string) => `${arrayPath}[${index}].${field}`;
    if (!entry.once_start_date) {
      errors.push(new Yup.ValidationError("Start date is required.", entry, at("once_start_date")));
    }
    if (!entry.once_end_date) {
      errors.push(new Yup.ValidationError("End date is required.", entry, at("once_end_date")));
    } else if (entry.once_start_date && new Date(entry.once_end_date) <= new Date(entry.once_start_date)) {
      errors.push(new Yup.ValidationError("End date must be after the start date.", entry, at("once_end_date")));
    }
  });
  return errors;
};

export const EditContactOnCallHoursSchema = Yup.object({
  schedule_entries: Yup.array()
    .defined()
    .when("schedule_state", {
      is: ScheduleState.SCHEDULED,
      then: (schema) =>
        schema.test("once-dates", "", (entries, context) => {
          const errors = onceDateErrors(entries, context.path);
          return errors.length > 0 ? context.createError({message: () => errors}) : true;
        }),
    }),
  schedule_state: Yup.string().oneOf(Object.values(ScheduleState)),
});

export const EditContactOnCallHoursForm = () => {
  const {watch} = useFormContext();
  const {user} = useCurrentUserContext();
  const scheduleState = watch("schedule_state");

  return (
    <>
      <CheckFormInfo
        title="On-Call hours indicate when this Contact will be notified if a Check fails."
        className="mb-3"
      />
      <Card>
        <FormLabel forId="schedule_state" required className="mb-0">
          Set On-Call Hours
        </FormLabel>
        <Divider className="my-3" />
        <OnCallHoursRadioGroup />
        {scheduleState === ScheduleState.SCHEDULED && (
          <ScheduleEntries className="mt-3" timezone={user!.account!.timezone} />
        )}
      </Card>
    </>
  );
};
